import React, { useCallback, useEffect, useRef } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useAuth } from '../../../context/auth-provider';
import { ClockSurfaceProvider, publishBuildingZones } from '../../../context/clock-context';
import { getTenantOverview } from '../../../api/tenant-client';
import { ContentTransition } from '../../route-transition/route-transition';
import { IndicatorGroup } from '../../../motion';
import { LogoMark } from '../../logo/logo-mark';
import { LogoutIcon } from '../../icons/log-out';
import { AppButton } from '../../button/app-button';
import { usePortalLiveRefresh } from '../../hooks/use-portal-live-refresh';
import {
  LIVE_PORTAL_EVENTS,
  PortalAccessContext,
  PortalOverviewContext,
  usePanel,
} from './portal-common';
import { PortalHome } from './portal-home';
import { PortalParcels } from './portal-parcels';
import { PortalVisitors } from './visitors/portal-visitors';
import { PortalPassDetail } from './visitors/pass-detail';
import { PortalIq } from './portal-iq';
import {
  BarIndicator,
  BarItem,
  BarLabel,
  BottomBar,
  Brand,
  Content,
  Page,
  Tab,
  TabIndicator,
  TabLabel,
  Tabs,
  TopActions,
  TopBar,
  Wordmark,
} from './portal-shell.styled';

/**
 * THE RESIDENT PORTAL — one shell, three destinations: Home, Visitors, Parcels.
 *
 * It was one page — headline, parcels, visitors, home — and a resident on a
 * phone scrolled past forty collected parcels to reach their own visitors. The
 * shell keeps the brand bar, the landmark and the overview mounted, and only
 * the tab's content moves (ContentTransition), exactly as the account page
 * does. There is room for more tabs; the visitor-pass plans add to Visitors.
 *
 * ── ONE ROUTE ENTRY, NESTED ROUTES ──
 * Registered once as '/portal/*' (page-routes.tsx), so ONE key in
 * ROUTE_PERMISSIONS gates every portal path, nested ones included — the map is
 * an exact-key lookup on the entry's path. '/tenant-dashboard' stays a route
 * forever and redirects here: stored notification rows and push notifications
 * deep-link to it.
 *
 * ── EVERY LINK AND REDIRECT IN HERE IS ABSOLUTE, AND HAS TO BE ──
 * App.tsx opts the router into `v7_relativeSplatPath`, under which a RELATIVE
 * `to` inside a splat route resolves against the full matched location: a tab
 * `to="parcels"` pressed at /portal/visitors would go to
 * /portal/visitors/parcels, match nothing, and hit the catch-all. That is
 * exactly how every /account tab once went dead while a deep-link-only test
 * stayed green. An absolute path resolves the same under both behaviours.
 */
const PORTAL_BASE = '/portal';

interface SectionDef {
  /** '' is Home, the index route. */
  path: string;
  label: string;
  element: React.ReactNode;
}

/** The section's real URL. One place, so a tab and a redirect cannot disagree. */
const sectionHref = (path: string): string =>
  path ? `${PORTAL_BASE}/${path}` : PORTAL_BASE;

const SECTIONS: SectionDef[] = [
  { path: '', label: 'Home', element: <PortalHome /> },
  { path: 'visitors', label: 'Visitors', element: <PortalVisitors /> },
  { path: 'parcels', label: 'Parcels', element: <PortalParcels /> },
];

/* WHICH LAYER A LOCATION BELONGS TO, for the content transition. An unknown
   sub-path REDIRECTS to Home, so it is keyed as Home already: the redirect then
   swaps the route inside one layer instead of playing an exit for a screen
   that was never going to be shown. */
const sectionKeyFor = (pathname: string): string => {
  const [, , segment = '', child = ''] = pathname.split('/');
  const known = segment && SECTIONS.some(section => section.path === segment);
  if (!known) return 'home';
  /* A pass's own page (/portal/visitors/:passId) is its own layer, so moving
     between the list and a pass swaps the content like a tab does. */

  return segment === 'visitors' && child ? `visitors/${child}` : segment;
};

/**
 * The stored-link redirect. '/tenant-dashboard' is carried by notification
 * rows already in the database and by push notifications already on phones, so
 * it is kept as a route for good — it just lands here.
 */
export const LegacyPortalRedirect: React.FC = () => <Navigate to={PORTAL_BASE} replace />;

export const PortalShell: React.FC = () => {
  const { logout, refreshAuth } = useAuth();
  const navigate = useNavigate();
  /* A 403 ON A PORTAL READ: the reader's last home has gone (RP-9.9). Ask the
     session ONCE what it may still do — the route guard above this shell then
     swaps the portal for the "no home" screen, instead of every panel offering
     a "Try again" that can only fail. Once per visit: if the refusal was about
     something else, the panels say so and nothing loops. */
  const asked = useRef(false);
  const onForbidden = useCallback(() => {
    if (asked.current) return;
    asked.current = true;
    void refreshAuth();
  }, [refreshAuth]);
  /* Read here and handed to <Routes>: the leaving tab keeps rendering for its
     exit, and pinning the location is what stops it from matching the arriving
     route mid-animation (see route-transition.tsx). */
  const location = useLocation();
  const sectionKey = sectionKeyFor(location.pathname);
  /* The overview is read once, here, for every tab: Home renders it and the
     list tabs take the building's time zone from it. */
  const overview = usePanel(getTenantOverview, onForbidden);
  /* Here, not on Home: the headline must be current when the resident comes
     BACK to Home, and the shell is what stays mounted while they are away. */
  usePortalLiveRefresh(LIVE_PORTAL_EVENTS, overview.reload);
  /* A resident never loads the staff buildings list, so the zones of their
     homes' buildings are published from here: whatever looks a building's
     clock up by id (the notification bell) then finds a resident's too. */
  const homes = overview.data?.homes;
  useEffect(() => {
    if (!homes?.length) return;
    publishBuildingZones(
      homes.map(home => ({ id: home.building_id, timezone: home.building_timezone }))
    );
  }, [homes]);

  return (
    /* A BUILDING-CLOCK SURFACE (R-T12): everything in the portal is on the
       building's clock for whoever is in it — a manager who also lives here is
       a resident here. */
    <ClockSurfaceProvider surface="portal">
    <PortalAccessContext.Provider value={onForbidden}>
    <PortalOverviewContext.Provider value={overview}>
      <Page>
        <TopBar>
          <Brand>
            <LogoMark size={28} aria-hidden="true" />
            <Wordmark>Lobbero</Wordmark>
          </Brand>
          {/* The bell is NOT here. It is NotificationDock, mounted once in
              App.tsx and pinned top-right on every screen; this bar reserves
              room for it (layout.bellDockReserve). */}
          <TopActions>
            {/* The account area holds details, password, devices and
                notifications behind this one button. */}
            <AppButton variant="secondary" onClick={() => navigate('/account/profile')}>
              <span>Account</span>
            </AppButton>
            <AppButton variant="secondary" onClick={() => logout()}>
              <LogoutIcon />
              <span>Sign out</span>
            </AppButton>
          </TopActions>
        </TopBar>

        {/* The skip link, mounted globally in App.tsx, resolves to this. */}
        <Content id="main-content" tabIndex={-1}>
          {/* md and up. Below md the same three live in the bottom bar — one
              or the other, never both (display, so the hidden one is out of
              the accessibility tree too). */}
          <Tabs aria-label="Resident portal">
            <IndicatorGroup id="portal-tabs">
              {SECTIONS.map(section => (
                <Tab
                  key={section.label}
                  to={sectionHref(section.path)}
                  end={!section.path}
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <TabIndicator id="portal-tabs" data-active-indicator="true" />
                      )}
                      <TabLabel>{section.label}</TabLabel>
                    </>
                  )}
                </Tab>
              ))}
            </IndicatorGroup>
          </Tabs>

          <ContentTransition contentKey={sectionKey} direction="none">
            <Routes location={location}>
              {SECTIONS.map(section =>
                section.path ? (
                  <Route
                    key={section.label}
                    path={section.path}
                    element={section.element}
                  />
                ) : (
                  <Route key={section.label} index element={section.element} />
                )
              )}
              {/* One visitor pass, read only (resident-portal Plan 3). Nested
                  here so the '/portal/*' permission key gates it and the
                  Visitors tab stays lit (its link is not `end`). */}
              <Route path="visitors/:passId" element={<PortalPassDetail />} />
              {/* Anything else under /portal is a stale or mistyped link. Land
                  on Home rather than on a blank page — ABSOLUTE, because a
                  relative redirect appends to a path that already failed to
                  match and loops. */}
              <Route path="*" element={<Navigate to={PORTAL_BASE} replace />} />
            </Routes>
          </ContentTransition>
        </Content>

        {/* Phones. The desk rail's bottom-bar material: fixed, frosted, clear
            of the home indicator and the Android gesture bar. */}
        <BottomBar aria-label="Resident">
          <IndicatorGroup id="portal-bar">
            {SECTIONS.map(section => (
              <BarItem
                key={section.label}
                to={sectionHref(section.path)}
                end={!section.path}
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <BarIndicator id="portal-bar" data-active-indicator="true" />
                    )}
                    <BarLabel>{section.label}</BarLabel>
                  </>
                )}
              </BarItem>
            ))}
          </IndicatorGroup>
        </BottomBar>

        {/* Lobbero IQ for the resident: the desk's launcher, in the desk's
            corner, answering only about the reader's own home. Renders
            nothing unless the server says it is switched on. */}
        <PortalIq />
      </Page>
    </PortalOverviewContext.Provider>
    </PortalAccessContext.Provider>
    </ClockSurfaceProvider>
  );
};
